import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Shield,
  Send,
  Check,
  AlertTriangle,
  Loader2,
  Camera,
  X,
} from 'lucide-react';
import { todayLocalISO, nowLocalHHMM } from '@/utils/dateUtils';
import { validateMobile } from '@/utils/inputSanitizers';
import PhoneInput from '@/components/PhoneInput';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Observations (UA/UC) route to the Observations module server-side; the
// remaining types create incidents. The retired plain 'Observation' type is
// gone — anonymous reporters pick UA or UC directly.
const OBSERVATION_TYPES = [
  { value: 'Unsafe Act', label: 'UA – Unsafe Act' },
  { value: 'Unsafe Condition', label: 'UC – Unsafe Condition' },
];

const INCIDENT_TYPES = [
  { value: 'Near-Miss', label: 'Near-Miss' },
  { value: 'First Aid', label: 'First Aid' },
  { value: 'Medical Treatment', label: 'Medical Treatment' },
  { value: 'Restricted Work', label: 'Restricted Work' },
  { value: 'Lost Time Injury', label: 'Lost Time Injury' },
  { value: 'Fatality', label: 'Fatality' },
  { value: 'Property Damage', label: 'Property Damage' },
  { value: 'Environmental', label: 'Environmental' },
  { value: 'Equipment Failure', label: 'Equipment Failure' },
  { value: 'Occupational Disease', label: 'Occupational Disease' },
  { value: 'Occupational Poisoning', label: 'Occupational Poisoning' },
  { value: 'Other', label: 'Other' },
];

const SEVERITIES = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Critical', label: 'Critical' },
];

const MAX_FILES = 10;
const MAX_FILE_SIZE_MB = 10;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

const AnonymousReport = () => {
  const { slug, token } = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [incidentNumber, setIncidentNumber] = useState('');
  const [error, setError] = useState('');
  const [photoUploadFailed, setPhotoUploadFailed] = useState(false);

  const [form, setForm] = useState({
    title: '',
    description: '',
    hazard_type: '',
    severity: '',
    // Local wall clock, not UTC — a reporter ahead of UTC would otherwise be
    // prefilled with (and capped at) yesterday's date.
    incident_date: todayLocalISO(),
    incident_time: nowLocalHHMM(),
    location: '',
    reporter_name: '',
    reporter_email: '',
    reporter_mobile: '',
  });

  const [validation, setValidation] = useState({});
  const [mobileCountry, setMobileCountry] = useState('OTHER');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);
  const fileInputRef = useRef(null);

  const [siteName, setSiteName] = useState('');
  const [siteScoped, setSiteScoped] = useState(false);
  const [siteLoading, setSiteLoading] = useState(true);

  useEffect(() => {
    if (!slug || !token) {
      setSiteLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/public/report/${slug}/${token}/sites`);
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          if (data.token_site_scoped && Array.isArray(data.sites) && data.sites.length > 0) {
            setSiteScoped(true);
            setSiteName(data.sites[0].name || '');
          }
        }
      } catch (err) {
        // Fall through — the form still submits; invalid tokens are caught at submit time
      } finally {
        if (!cancelled) setSiteLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, token]);

  // REG-13: the country options / validateMobile rules now live in
  // utils/inputSanitizers.js and the picker UI in components/PhoneInput.jsx.

  const validate = () => {
    const errors = {};
    if (!form.title || form.title.length < 5) errors.title = 'Title must be at least 5 characters';
    if (!form.description || form.description.length < 10) errors.description = 'Description must be at least 10 characters';
    if (!form.hazard_type) errors.hazard_type = 'Please select what you are reporting';
    if (!form.severity) errors.severity = 'Please select severity';
    if (!form.incident_date) errors.incident_date = 'Please select a date';
    if (form.reporter_email && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(form.reporter_email)) {
      errors.reporter_email = 'Please enter a valid email address';
    }
    const mobileError = validateMobile(form.reporter_mobile, mobileCountry);
    if (mobileError) errors.reporter_mobile = mobileError;
    setValidation(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    if (validation[name]) {
      setValidation(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const remaining = MAX_FILES - selectedFiles.length;
    if (remaining <= 0) {
      setError(`Maximum ${MAX_FILES} photos allowed.`);
      return;
    }

    const validFiles = [];
    for (const file of files.slice(0, remaining)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setError(`${file.name}: Only JPG, PNG, and GIF images are allowed.`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setError(`${file.name}: File size exceeds ${MAX_FILE_SIZE_MB}MB limit.`);
        continue;
      }
      validFiles.push(file);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      // Generate previews
      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreviews(prev => [...prev, { name: file.name, url: reader.result }]);
        };
        reader.readAsDataURL(file);
      });
    }

    // Reset input so same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setError('');

    try {
      const payload = {
        title: form.title,
        description: form.description,
        hazard_type: form.hazard_type,
        severity: form.severity,
        incident_date: new Date(form.incident_date).toISOString(),
        incident_time: form.incident_time || null,
        location: form.location || null,
        reporter_name: form.reporter_name || null,
        reporter_email: form.reporter_email || null,
        reporter_mobile: form.reporter_mobile || null,
      };

      const response = await fetch(`${BACKEND_URL}/api/public/report/${slug}/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (response.status === 429) {
        setError('Too many reports submitted. Please try again in a few minutes.');
        return;
      }

      if (response.status === 404) {
        setError('This reporting link is invalid or has expired. Please contact the organization for a new link.');
        return;
      }

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        setError(errData.detail || 'Failed to submit report. Please try again.');
        return;
      }

      const data = await response.json();
      const hazardId = data.hazard_id;
      // UA/UC submissions are stored as observations and return an OBS-*
      // observation_number; everything else returns an INC-* incident_number.
      setIncidentNumber(
        data.observation_number
        || data.incident_number
        || (hazardId ? hazardId.substring(0, 8).toUpperCase() : 'SUBMITTED')
      );

      // Upload photos if any were selected
      if (selectedFiles.length > 0 && hazardId) {
        let uploadAttempts = 0;
        const maxRetries = 2;
        let uploadSuccess = false;

        while (uploadAttempts <= maxRetries && !uploadSuccess) {
          try {
            const formData = new FormData();
            selectedFiles.forEach(file => {
              formData.append('files', file);
            });

            const uploadResponse = await fetch(
              `${BACKEND_URL}/api/public/report/${slug}/${token}/${hazardId}/upload-photos`,
              {
                method: 'POST',
                body: formData,
              }
            );

            if (uploadResponse.ok) {
              uploadSuccess = true;
            } else {
              uploadAttempts++;
              if (uploadAttempts > maxRetries) {
                console.warn('Photo upload failed after retries, but incident report was submitted successfully.');
                setPhotoUploadFailed(true);
              }
            }
          } catch (uploadErr) {
            uploadAttempts++;
            if (uploadAttempts > maxRetries) {
              console.warn('Photo upload error after retries:', uploadErr);
              setPhotoUploadFailed(true);
            }
          }
        }
      }

      setSubmitted(true);
    } catch (err) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Report Submitted</h2>
            <p className="text-gray-600">
              Thank you for helping improve safety. Your report has been received.
            </p>
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-sm text-gray-500">Reference Number</span>
              <p className="text-lg font-mono font-semibold text-[#1B5C94]">{incidentNumber}</p>
            </div>
            {photoUploadFailed && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-700">
                  Your report was submitted, but photos could not be uploaded. Please contact the safety team to share your photos separately.
                </p>
              </div>
            )}
            <p className="text-xs text-gray-400">
              You may close this page. The safety team has been notified.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-[#1B5C94] flex items-center justify-center mb-3">
            <Shield className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900">Report a Safety Issue</h1>
          <p className="text-sm text-gray-500 mt-1">
            Your identity is not required. All reports are treated confidentially.
          </p>
        </div>

        {/* Error */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Incident Details</CardTitle>
            <CardDescription>Fields marked with * are required.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Site (auto-set from QR code token — readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Site <span className="text-red-500">*</span>
                </label>
                {siteLoading ? (
                  <div className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-500">
                    Loading...
                  </div>
                ) : siteScoped && siteName ? (
                  <div className="w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-700">
                    {siteName}
                  </div>
                ) : (
                  <div className="w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                    Site not specified for this reporting link.
                  </div>
                )}
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Brief description of the incident"
                  maxLength={255}
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94] ${
                    validation.title ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {validation.title && (
                  <p className="text-xs text-red-500 mt-1">{validation.title}</p>
                )}
              </div>

              {/* What are you reporting? (observation vs incident) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  What are you reporting? <span className="text-red-500">*</span>
                </label>
                <select
                  name="hazard_type"
                  value={form.hazard_type}
                  onChange={handleChange}
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94] ${
                    validation.hazard_type ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select type...</option>
                  <optgroup label="Observation">
                    {OBSERVATION_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </optgroup>
                  <optgroup label="Incident">
                    {INCIDENT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </optgroup>
                </select>
                {validation.hazard_type && (
                  <p className="text-xs text-red-500 mt-1">{validation.hazard_type}</p>
                )}
              </div>

              {/* Severity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Severity <span className="text-red-500">*</span>
                </label>
                <select
                  name="severity"
                  value={form.severity}
                  onChange={handleChange}
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94] ${
                    validation.severity ? 'border-red-300' : 'border-gray-300'
                  }`}
                >
                  <option value="">Select severity...</option>
                  {SEVERITIES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
                {validation.severity && (
                  <p className="text-xs text-red-500 mt-1">{validation.severity}</p>
                )}
              </div>

              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Incident <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="incident_date"
                    value={form.incident_date}
                    onChange={handleChange}
                    max={todayLocalISO()}
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94] ${
                      validation.incident_date ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {validation.incident_date && (
                    <p className="text-xs text-red-500 mt-1">{validation.incident_date}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Time of Incident
                  </label>
                  <input
                    type="time"
                    name="incident_time"
                    value={form.incident_time || ''}
                    onChange={handleChange}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Describe what happened, where, and any injuries or damage..."
                  maxLength={5000}
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94] ${
                    validation.description ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <div className="flex justify-between">
                  {validation.description && (
                    <p className="text-xs text-red-500 mt-1">{validation.description}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1 ml-auto">{form.description.length}/5000</p>
                </div>
              </div>

              {/* Location (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  placeholder="e.g. Building A, Floor 2, Workshop"
                  maxLength={255}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                />
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Photos (optional)
                </label>
                <div
                  className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-[#1B5C94] transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Camera className="h-6 w-6 mx-auto text-gray-400 mb-1" />
                  <p className="text-sm text-gray-500">
                    Tap to add photos
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    JPG, PNG or GIF. Max {MAX_FILE_SIZE_MB}MB each. Up to {MAX_FILES} photos.
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                {/* Photo Previews */}
                {filePreviews.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-3">
                    {filePreviews.map((preview, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={preview.url}
                          alt={preview.name}
                          className="w-full h-20 object-cover rounded-md border border-gray-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                        <p className="text-[10px] text-gray-400 truncate mt-0.5">{preview.name}</p>
                      </div>
                    ))}
                  </div>
                )}
                {selectedFiles.length > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedFiles.length} photo{selectedFiles.length !== 1 ? 's' : ''} selected
                  </p>
                )}
              </div>

              {/* Reporter Name (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Your Name (optional)</label>
                <input
                  type="text"
                  name="reporter_name"
                  value={form.reporter_name}
                  onChange={handleChange}
                  placeholder="Leave blank to remain anonymous"
                  maxLength={255}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                />
              </div>

              {/* Reporter Email (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email (optional)</label>
                <input
                  type="email"
                  name="reporter_email"
                  value={form.reporter_email}
                  onChange={handleChange}
                  placeholder="your.email@example.com"
                  maxLength={255}
                  className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94] ${
                    validation.reporter_email ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {validation.reporter_email && (
                  <p className="text-xs text-red-500 mt-1">{validation.reporter_email}</p>
                )}
              </div>

              {/* Reporter Mobile (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile (optional)</label>
                {/* REG-13: shared PhoneInput. digitsOnly={false} keeps the
                    submitted value exactly as typed (payload unchanged);
                    validation still runs on submit via validateMobile. */}
                <PhoneInput
                  name="reporter_mobile"
                  value={form.reporter_mobile}
                  onChange={(v) => handleChange({ target: { name: 'reporter_mobile', value: v } })}
                  country={mobileCountry}
                  onCountryChange={setMobileCountry}
                  digitsOnly={false}
                  maxLength={20}
                  validateOnBlur={false}
                  error={validation.reporter_mobile}
                  selectClassName="rounded-md border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94] shrink-0"
                  inputClassName="rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                />
              </div>

              {/* Submit */}
              <Button
                type="submit"
                className="w-full bg-[#1B5C94] hover:bg-[#154a78]"
                disabled={submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Submit Report
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Privacy note */}
        <p className="text-center text-xs text-gray-400">
          Your IP address is logged for abuse prevention only.
          No account or personal information is required.
        </p>
      </div>
    </div>
  );
};

export default AnonymousReport;
